import { useDeclineCall } from '@doneverse/data/hooks'
import { contactsOptions } from '@doneverse/data/queries'
import type { CallRingState } from '@doneverse/shared'
import { useQuery } from '@tanstack/react-query'
import { Phone, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useStartOrJoinCall } from './useStartOrJoinCall'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { hasNativeCalls } from '@/lib/mobile-bridge'
import { startRingtone, stopRingtone } from '@/lib/notification-sound'
import { getInitials } from '@/lib/utils'
import { myRingState, useDashboardStore, type IncomingCall } from '@/store'

/**
 * How long an incoming call rings (popup pulsing + looping sound) before it
 * settles into the quiet "Ongoing call" variant. Measured from the call's
 * `started_at`, NOT from when this tab learned about it — so a reload or a
 * REST hydration of an old call never re-rings, and every tab goes quiet at
 * the same moment.
 *
 * This is a SOUND deadline only. Whether the user still owes the call an answer
 * is server state (`ring`), not a timer — which is what lets answering on one
 * device silence every other one instantly.
 */
const RING_DURATION_MS = 60_000

interface VisibleCall {
  contactId: string
  call: IncomingCall
  /** This user's server-side ring state for the call. */
  ringState: CallRingState
}

function useVisibleCalls(): VisibleCall[] {
  const incomingCalls = useDashboardStore((s) => s.incomingCalls)
  const activeCall = useDashboardStore((s) => s.activeCall)
  const currentUserId = useDashboardStore((s) => s.currentUserId)

  return Object.entries(incomingCalls)
    .map(([contactId, call]) => ({
      contactId,
      call,
      ringState: myRingState(call, currentUserId),
    }))
    .filter(
      ({ call, ringState }) =>
        // Declined is a real, server-persisted decision — unlike the local
        // dismissal this replaced, it survives a reload and every other device.
        ringState !== 'declined' &&
        // Already in it — the dock is the surface for that.
        activeCall?.callId !== call.callId &&
        // Our own `call.started` event comes back to us too: never ring the caller.
        call.startedById !== currentUserId,
    )
    .sort((a, b) => Date.parse(b.call.startedAt) - Date.parse(a.call.startedAt))
}

/**
 * The set of visible calls that should actually be RINGING, self-updating.
 *
 * Two conditions, and the first is the point of this whole feature: the server
 * still considers us un-answered. Answering on a phone flips that to `answered`
 * for every device at once, so the laptop stops mid-ring instead of grinding
 * through the rest of its 60 seconds.
 */
function useRingingCallIds(visible: VisibleCall[]): Set<string> {
  const [now, setNow] = useState(() => Date.now())

  const unanswered = visible.filter((v) => v.ringState === 'ringing')
  const ringExpiry = ({ call }: VisibleCall) => Date.parse(call.startedAt) + RING_DURATION_MS
  const ringing = unanswered.filter((v) => ringExpiry(v) > now)
  const nextExpiry = ringing.length > 0 ? Math.min(...ringing.map(ringExpiry)) : null

  // One timer to the nearest ring expiry; each firing re-derives the set.
  // Keyed on the primitive so unrelated re-renders don't reset the timer.
  useEffect(() => {
    if (nextExpiry === null) return
    const timer = setTimeout(() => setNow(Date.now()), nextExpiry - Date.now() + 50)
    return () => clearTimeout(timer)
  }, [nextExpiry])

  return new Set(ringing.map(({ call }) => call.callId))
}

/**
 * The app-wide incoming-call surface: a popup (with a looping ringtone for the
 * first 60s) for every live call in a conversation this user is part of but
 * hasn't joined. Mounted once at the app root next to CallHost, outside the
 * router, so a call reaches the user no matter what page they're on — the
 * in-chat banner only ever covered the one thread the user was staring at.
 *
 * Declining tells the server: the ring stops on every one of this user's
 * devices, the caller sees it, and a DM call ends outright.
 */
export function IncomingCallHost() {
  const { hasFlag } = useFeatureFlags()
  const enabled = hasFlag('huddles')

  const visible = useVisibleCalls()
  const ringingIds = useRingingCallIds(visible)
  const activeCall = useDashboardStore((s) => s.activeCall)
  const soundEnabled = useDashboardStore((s) => s.notificationSoundEnabled)
  const nativeCalls = hasNativeCalls()

  // Conversation names for the subtitle. Usually already in the cache (the
  // contact list fetches it); a miss just omits the subtitle. A cache read,
  // not a live query — the popup must never generate contacts refetches.
  const { data: contacts } = useQuery({
    ...contactsOptions(),
    enabled: enabled && visible.length > 0,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  })

  // Ring while any visible call is in its ring window — but never over a call
  // we're already in, and never against the user's sound preference.
  const shouldRing = !nativeCalls && enabled && soundEnabled && activeCall === null && ringingIds.size > 0
  useEffect(() => {
    if (!shouldRing) return
    startRingtone()
    return () => stopRingtone()
  }, [shouldRing])

  if (nativeCalls || !enabled || visible.length === 0) return null

  return createPortal(
    // Above the call dock (z-50), below dialogs (z-[1000]).
    <div className="fixed right-4 top-4 z-[60] flex w-[340px] flex-col gap-2" aria-live="polite">
      {visible.map(({ contactId, call, ringState }) => (
        <IncomingCallCard
          key={call.callId}
          contactId={contactId}
          call={call}
          ringing={ringingIds.has(call.callId)}
          ringState={ringState}
          conversationName={contacts?.find((c) => c.id === contactId)?.display_name}
        />
      ))}
    </div>,
    document.body,
  )
}

interface IncomingCallCardProps {
  contactId: string
  call: IncomingCall
  ringing: boolean
  ringState: CallRingState
  conversationName: string | undefined
}

function IncomingCallCard({ contactId, call, ringing, ringState, conversationName }: IncomingCallCardProps) {
  const removeCallById = useDashboardStore((s) => s.removeCallById)
  const declineOwnRing = useDashboardStore((s) => s.declineOwnRing)
  const declineCall = useDeclineCall()
  const { join, isJoining, blocked } = useStartOrJoinCall(contactId, conversationName ?? call.startedByName)

  // Picked up somewhere else. The popup stays — turning it into an offer to
  // move the call here rather than vanishing, because "I answered on my phone
  // but I'd rather be on my laptop" is the common next thought.
  const answeredElsewhere = ringState === 'answered'

  const decline = () => {
    // Mark it declined locally so the popup goes on the click, not on the round
    // trip. Deliberately NOT dropping the call: declining silences the
    // interruptive popup, it doesn't retract the conversation's passive
    // "there's a call, join it" affordances — you may well change your mind.
    declineOwnRing(contactId)
    declineCall.mutate(call.callId)
  }

  // Don't repeat the caller's name as the subtitle in a DM.
  const subtitle = conversationName && conversationName !== call.startedByName ? conversationName : undefined

  return (
    <div
      className="animate-in fade-in slide-in-from-top-2 rounded-2xl bg-white p-3 shadow-[0_0_0_1px_rgba(0,0,0,0.06),0_4px_12px_rgba(0,0,0,0.1),0_12px_32px_rgba(0,0,0,0.12)] duration-300"
      data-testid="incoming-call-popup"
      role={ringing ? 'alert' : undefined}
    >
      <div className="flex items-center gap-3">
        <div className="relative h-10 w-10 shrink-0">
          {ringing && <span className="absolute inset-0 animate-ping rounded-full bg-green-500 opacity-25" />}
          <Avatar>
            <AvatarFallback className="bg-green-100 text-[13px] font-semibold text-green-800">
              {getInitials(call.startedByName)}
            </AvatarFallback>
          </Avatar>
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-[13px] leading-5 text-foreground">
            <strong className="font-semibold">{call.startedByName}</strong>{' '}
            {ringing ? 'is calling' : 'started a call'}
          </p>
          <p className="truncate text-[12px] leading-4 text-muted-foreground">
            {subtitle ? `${subtitle} · ` : ''}
            {answeredElsewhere ? 'Answered on another device' : ringing ? 'Incoming call' : 'Ongoing call'}
          </p>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0 text-muted-foreground transition-transform active:scale-[0.96]"
          aria-label={answeredElsewhere ? 'Hide call' : 'Decline call'}
          data-testid="incoming-call-dismiss"
          onClick={answeredElsewhere ? () => removeCallById(call.callId) : decline}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="mt-2.5">
        <Button
          size="sm"
          className="h-9 w-full gap-1.5 bg-green-600 text-[13px] transition-transform hover:bg-green-700 active:scale-[0.96]"
          disabled={isJoining || blocked}
          title={blocked ? 'Leave your current call first' : undefined}
          data-testid="incoming-call-join"
          onClick={join}
        >
          <Phone className="h-3.5 w-3.5" />
          {isJoining ? 'Joining…' : answeredElsewhere ? 'Switch here' : 'Join'}
        </Button>
      </div>
    </div>
  )
}
